import { RefObject, useCallback, useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

interface UseAboutCledarAnimationProps {
  sectionRef: RefObject<HTMLDivElement | null>;
  mobilePathRef: RefObject<SVGPathElement | null>;
  desktopPathRef: RefObject<SVGPathElement | null>;
  scrollTriggerRef?: RefObject<HTMLDivElement | null>;
}

export const useAboutCledarAnimation = ({
  sectionRef,
  mobilePathRef,
  desktopPathRef,
  scrollTriggerRef,
}: UseAboutCledarAnimationProps) => {
  const timelineRef = useRef<gsap.core.Timeline | null>(null);

  const initAnimation = useCallback(() => {
    if (timelineRef.current) {
      timelineRef.current.kill();
      timelineRef.current = null;
    }

    const triggerElement = scrollTriggerRef?.current ?? sectionRef.current;
    if (!triggerElement) return;

    const paths = [mobilePathRef.current, desktopPathRef.current].filter(
      Boolean
    ) as SVGPathElement[];

    if (paths.length === 0) return;

    paths.forEach((path) => {
      const pathLength = path.getTotalLength();
      gsap.set(path, {
        strokeDasharray: pathLength,
        strokeDashoffset: pathLength,
      });
    });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: triggerElement,
        start: "15% top",
        end: "28% top",
        scrub: 1,
      },
    });

    paths.forEach((path) => {
      tl.to(
        path,
        {
          strokeDashoffset: 0,
          duration: 0.8,
          ease: "none",
        },
        0
      );
    });

    timelineRef.current = tl;
  }, [scrollTriggerRef, sectionRef, mobilePathRef, desktopPathRef]);

  useEffect(() => {
    return () => {
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
    };
  }, []);

  return { initAnimation };
};
